import React from "react";
import { Circle, CircleDashed, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthContext";

export function StatusPill({ status }) {
  const s = (status || "").toLowerCase();
  const cls = s === "green" || s === "approved" || s === "confirmed" ? "ag-pill-green"
    : s === "amber" || s === "sent" || s === "checking" ? "ag-pill-amber"
      : s === "red" || s === "not available" ? "ag-pill-red"
        : s === "draft" || s === "preferred" ? "ag-pill-neutral" : "ag-pill-neutral";
  return <span className={`ag-pill ${cls}`} data-testid="agency-status-pill">{status}</span>;
}

// Open Items are discrete action items, so they use workflow states (progress), not RAG health.
// Text + icon (never colour alone) so state is legible regardless of colour perception.
export function OpenItemStatus({ status }) {
  const s = (status || "").toLowerCase().replace(/[\s_]+/g, "-");
  let Icon = Circle, label = "Yet to begin", color = "var(--faint)";
  if (s.includes("complete") || s.includes("done")) { Icon = CheckCircle2; label = "Complete"; color = "var(--green)"; }
  else if (s.includes("progress")) { Icon = CircleDashed; label = "In progress"; color = "var(--amber)"; }
  else if (s.includes("yet") || s.includes("not-started") || s.includes("todo") || s === "" ) { Icon = Circle; label = "Yet to begin"; color = "var(--faint)"; }
  else { Icon = Circle; label = status; color = "var(--faint)"; }
  return (
    <span className="ag-pill ag-pill-neutral" style={{ display: "inline-flex", alignItems: "center", gap: 6 }} data-testid="agency-openitem-status">
      <Icon size={13} color={color} strokeWidth={2.25} /> {label}
    </span>
  );
}

export function Lifecycle({ timeline }) {
  if (!timeline?.length) return null;
  return (
    <div className="ag-timeline" data-testid="agency-lifecycle">
      {timeline.map((s) => (
        <div key={s.stage} className={`ag-tl-step ${s.state}`}>
          <span className="ag-tl-dot" />
          <span className="ag-tl-name">{s.stage}</span>
        </div>
      ))}
    </div>
  );
}

export function greeting() {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
}

export function firstName(name) {
  // Wave 1 (2026-10) bug fix: "Trifilm, Inc".split(/[\s@]/)[0] was "Trifilm," — the trailing
  // comma rode straight into the greeting ("Good morning, Trifilm,."). Strip trailing
  // punctuation off the first token.
  return (name || "there").split(/[\s@]/)[0].replace(/[,.;:!?]+$/, "") || "there";
}

// Active vs "Delivered" quieter group (Home + Projects list, Wave 1 2026-10) and the chip
// label shown for it — `_agency_current_stage()` (modules/build.py) already collapses
// Delivered/Archived/Lost into the single stage name "Delivery"; the design calls that state
// "Delivered" on the chip, so that's a display-only rename, not a new backend state.
// Cursor fix, item 9 (2026-10): a Lost project still groups into this SAME quiet section
// (`isDelivered` unchanged) but must never be labeled "Delivered" — pass the backend's `lost`
// flag through and it renders "Closed" instead.
export function isDelivered(stageOrStatus) { return (stageOrStatus || "") === "Delivery"; }
export function stageChipLabel(stageOrStatus, lost) {
  if (isDelivered(stageOrStatus)) return lost ? "CLOSED" : "DELIVERED";
  return (stageOrStatus || "").toUpperCase();
}

// Reusable hover/focus tooltip — used by the DRAFT chip ("Not yet shared with NJM.") on
// Overview/Preferred Crew (Wave 1, 2026-10).
export function Tooltip({ label, children }) {
  const [open, setOpen] = React.useState(false);
  return (
    <span className="ag-tooltip-wrap" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)} onFocus={() => setOpen(true)} onBlur={() => setOpen(false)} tabIndex={0} data-testid="agency-tooltip-wrap">
      {children}
      {open && <span className="ag-tooltip-bubble" role="tooltip" data-testid="agency-tooltip-bubble">{label}</span>}
    </span>
  );
}

export function DraftChip() {
  return (
    <Tooltip label="Not yet shared with NJM.">
      <span className="ag-pill ag-pill-neutral" data-testid="agency-draft-chip">DRAFT</span>
    </Tooltip>
  );
}

// Part B (2026-10), item 4 — ROOT CAUSE: a plain `<a href="mailto:...">` unconditionally hands
// off to the OS/browser's mail-client protocol handler. In ANY session with no default mail
// app registered — which describes whoever is just staff-previewing a read-only simulation of
// the agency portal, not actually the agency — that native handoff can hang with no visible
// feedback and no error dialog at all: it just looks like the screen froze. Preview mode has no
// real reason to send mail as "the agency" anyway, so the handoff is skipped there entirely —
// a toast instead, same component, same test id, zero behavior change for a REAL agency user.
export function handleAgencyMessageClick(e, isPreview) {
  if (!isPreview) return;
  e.preventDefault();
  toast.info("Messaging is disabled while previewing as the agency.");
}

// "Your NJM Producer" card (Wave 1, 2026-10) — Home (sticky) and Project Detail header both
// render this; both already had the data (GET /agency/pm-contact, modules/build.py's own
// "Your NJM producer" role_title), just weren't rendering the card yet.
// Cursor fix, item 6 (2026-10): also rendered as a slim footer bar on EVERY agency page
// (AgencyShell.jsx) so the Message (mailto) link the restyle had dropped is always reachable —
// `idPrefix` lets that extra instance use its own test ids (`agency-footer-pm-*`) instead of
// colliding with Home's existing `agency-producer-*` ones on the SAME page.
export function AgencyProducerCard({ pm, sticky, slim, idPrefix = "agency-producer" }) {
  const { user } = useAuth();
  if (!pm) return null;
  return (
    <div className={`ag-pm-card${sticky ? " ag-pm-card-sticky" : ""}${slim ? " ag-pm-card-slim" : ""}`} data-testid={`${idPrefix}-card`}>
      <div className="ag-pm-avatar">{(pm.name || "N").trim().charAt(0).toUpperCase()}</div>
      <div className="ag-pm-body">
        <div className="ag-pm-role">{pm.role || "Your NJM producer"}</div>
        <div className="ag-pm-name" data-testid={`${idPrefix}-name`}>{pm.name || "NJM Productions"}</div>
        {pm.email && (
          <a className="ag-pm-msg" href={`mailto:${pm.email}?subject=${encodeURIComponent("NJM Productions · Agency Portal")}`}
            onClick={(e) => handleAgencyMessageClick(e, user?.is_preview)} data-testid={`${idPrefix}-message`}>
            Message
          </a>
        )}
      </div>
    </div>
  );
}

// 2d — group the agency's visible documents into clear buckets so a project's SOWs, invoices and
// other agency files never read as one ambiguous pile.
export const DOC_BUCKET_ORDER = ["Agreements & SOW", "Invoices", "Agency documents"];
export function docBucket(docType) {
  const t = (docType || "").toLowerCase();
  if (t.includes("sow") || t.includes("agreement") || t.includes("contract")) return "Agreements & SOW";
  if (t.includes("invoice")) return "Invoices";
  return "Agency documents";
}
export function groupDocs(docs) {
  const by = {};
  for (const d of docs || []) (by[docBucket(d.doc_type)] ||= []).push(d);
  return DOC_BUCKET_ORDER.filter((b) => by[b]?.length).map((b) => ({ bucket: b, docs: by[b] }));
}

export function when(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const days = Math.round((Date.now() - d.getTime()) / 86400000);
    if (days <= 0) return "Today";
    if (days === 1) return "Yesterday";
    if (days < 7) return `${days} days ago`;
    return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
  } catch { return ""; }
}
